import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { MapPin, Stethoscope, Users, Loader2, ArrowRight } from "lucide-react";

export default function RepProfile() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [rep, setRep] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [lead, setLead] = useState({ practice_name: "", office_manager: "", address: "", email: "", phone: "", npi: "", patient_load: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/reps/${code}`).then(({ data }) => setRep(data)).catch(() => setNotFound(true));
  }, [code]);

  const setL = (k) => (e) => setLead({ ...lead, [k]: e.target.value });

  const submitLead = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/leads", { ...lead, rep_code: code });
      toast.success(data.message || "Thanks! We'll be in touch soon.");
      setLead({ practice_name: "", office_manager: "", address: "", email: "", phone: "", npi: "", patient_load: "" });
    } catch (err) {
      toast.error("Could not send your request. Please check the required fields and try again.");
    } finally {
      setSending(false);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-3xl mx-auto px-5 py-32 text-center">
          <h1 className="font-display text-3xl text-navy">Representative not found</h1>
        </div>
        <Footer />
      </div>
    );
  }
  if (!rep) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-navy" /></div>;

  const initials = (rep.name || "R").split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <section className="bg-navy-deep text-white afha-grain">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-16 flex flex-col sm:flex-row items-center gap-8">
          <div className="h-28 w-28 rounded-2xl bg-patriot flex items-center justify-center font-display text-4xl font-black shrink-0">{initials}</div>
          <div className="text-center sm:text-left">
            <p className="text-xs uppercase tracking-[0.2em] text-patriot-light">AFHA Representative</p>
            <h1 className="font-display font-black text-4xl mt-2">{rep.name}</h1>
            {(rep.city || rep.state) && (
              <p className="mt-2 flex items-center justify-center sm:justify-start gap-2 text-white/70"><MapPin className="h-4 w-4" /> {[rep.city, rep.state].filter(Boolean).join(", ")}</p>
            )}
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-14">
        {rep.bio && <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl">{rep.bio}</p>}
        <div className="mt-8 grid grid-cols-2 gap-5 max-w-lg">
          <div className="bg-white border border-border rounded-xl p-6">
            <Stethoscope className="h-6 w-6 text-navy mb-3" />
            <p className="font-display text-3xl text-navy">{rep.physicians}</p>
            <p className="text-sm text-muted-foreground">Physicians onboarded</p>
          </div>
          <div className="bg-white border border-border rounded-xl p-6">
            <Users className="h-6 w-6 text-navy mb-3" />
            <p className="font-display text-3xl text-navy">{rep.recruits}</p>
            <p className="text-sm text-muted-foreground">Direct recruits</p>
          </div>
        </div>

        <div className="mt-12 bg-white border border-border rounded-xl p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-display text-2xl text-navy">Join {rep.name?.split(" ")[0]}'s team</h2>
            <p className="text-muted-foreground mt-1">Become an AFHA representative under this sponsor.</p>
          </div>
          <Button onClick={() => navigate(`/signup?ref=${rep.referral_code}`)} data-testid="rep-join-btn" className="rounded-full bg-patriot hover:bg-patriot-dark text-white h-12 px-8 gap-2 shrink-0">
            Join with code {rep.referral_code} <ArrowRight className="h-4 w-4" />
          </Button>
        </div>

        <div className="mt-12 max-w-2xl" data-testid="rep-lead-section">
          <h2 className="font-display text-2xl text-navy">Request more information</h2>
          <p className="text-muted-foreground mt-1">Interested in GeneID testing for your practice? Send {rep.name?.split(" ")[0]} a message and they'll reach out.</p>
          <form onSubmit={submitLead} className="mt-6 bg-white border border-border rounded-xl p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="rep-lead-form">
            <div className="sm:col-span-2">
              <Label htmlFor="l-practice" className="text-navy">Practice name</Label>
              <Input id="l-practice" required value={lead.practice_name} onChange={setL("practice_name")} data-testid="lead-practice" className="mt-1.5 h-11" placeholder="Riverside Family Medicine" />
            </div>
            <div>
              <Label htmlFor="l-manager" className="text-navy">Office manager's name</Label>
              <Input id="l-manager" value={lead.office_manager} onChange={setL("office_manager")} data-testid="lead-manager" className="mt-1.5 h-11" placeholder="Jane Doe" />
            </div>
            <div>
              <Label htmlFor="l-npi" className="text-navy">NPI <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input id="l-npi" value={lead.npi} onChange={setL("npi")} data-testid="lead-npi" className="mt-1.5 h-11" placeholder="10-digit NPI" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="l-address" className="text-navy">Practice address</Label>
              <Input id="l-address" value={lead.address} onChange={setL("address")} data-testid="lead-address" className="mt-1.5 h-11" placeholder="123 Main St, Suite 200, City, State ZIP" />
            </div>
            <div>
              <Label htmlFor="l-email" className="text-navy">Email</Label>
              <Input id="l-email" type="email" required value={lead.email} onChange={setL("email")} data-testid="lead-email" className="mt-1.5 h-11" placeholder="office@practice.com" />
            </div>
            <div>
              <Label htmlFor="l-phone" className="text-navy">Phone</Label>
              <Input id="l-phone" required value={lead.phone} onChange={setL("phone")} data-testid="lead-phone" className="mt-1.5 h-11" placeholder="(555) 000-0000" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="l-load" className="text-navy">Average patient load per day</Label>
              <Input id="l-load" value={lead.patient_load} onChange={setL("patient_load")} data-testid="lead-load" className="mt-1.5 h-11" placeholder="e.g. 25" />
            </div>
            <div className="sm:col-span-2">
              <Button type="submit" disabled={sending} data-testid="lead-submit" className="w-full h-12 rounded-full bg-patriot hover:bg-patriot-dark text-white gap-2">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Send Request <ArrowRight className="h-4 w-4" /></>}
              </Button>
            </div>
          </form>
        </div>
      </section>
      <Footer />
    </div>
  );
}
